import { useState, useEffect, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { useAuth } from '../components/Auth/Auth';
import logger from '../utils/logger';
import { Dialog, Transition, Menu } from '@headlessui/react';
import {
  ChevronDownIcon,
  XMarkIcon,
  Bars3Icon,
  BellIcon,
  Cog6ToothIcon,
  UsersIcon,
  HomeIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import Ayden from '../images/ayden.jpg';
import { LogoutModal } from './Auth/LogoutModal';
import MoonLoader from 'react-spinners/MoonLoader';

const navigation = [
  { name: 'Home', href: '/', icon: HomeIcon },
  { name: 'Customers', href: '/customers', icon: UsersIcon },
  { name: 'Advisor', href: '/vehicles', icon: WrenchScrewdriverIcon },
  { name: 'Finance', href: '/finance', icon: CurrencyDollarIcon },
  { name: 'HR', href: '/humanresources', icon: UserGroupIcon },
  { name: 'Procurement', href: '/procurement', icon: ShoppingCartIcon },
  { name: 'Supervisor', href: '/supervisor', icon: ShieldCheckIcon },
  { name: 'Mechanics', href: '/mechanics', icon: WrenchScrewdriverIcon },
  { name: 'Reports', href: '/analytics', icon: ChartBarIcon },
  { name: 'Admin', href: '/admin', icon: Cog6ToothIcon },
  { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon },
];

const userNavigation = [
  { name: 'Your Profile', href: '/profile', action: 'navigate' },
  { name: 'Sign out', action: 'signOut' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Layout({ children }) {
  const { user } = useAuth();
  let navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasPendingRequests, setHasPendingRequests] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  useEffect(() => {
    const checkPendingLeaveRequests = async () => {
      if (user?.uid) {
        try {
          // Query employees where the current user is the designated manager
          const employeesQuery = query(
            collection(db, 'employees'),
            where('designatedManagerId', '==', user.uid)
          );

          const employeesSnapshot = await getDocs(employeesQuery);
          const employeeIds = employeesSnapshot.docs.map(doc => doc.id);

          if (employeeIds.length > 0) {
            // Query leaveRequests where the employeeId is in the list of managed employees and status is Pending
            const leaveRequestsQuery = query(
              collection(db, 'leaveRequests'),
              where('employeeId', 'in', employeeIds),
              where('status', '==', 'Pending')
            );

            const leaveRequestsSnapshot = await getDocs(leaveRequestsQuery);
            setHasPendingRequests(!leaveRequestsSnapshot.empty);
          } else {
            setHasPendingRequests(false);
          }
        } catch (error) {
          logger.error('Error checking pending leave requests:', error);
          setHasPendingRequests(false);
        }
      } else {
        setHasPendingRequests(false);
      }
      setLoading(false); 
    };

    checkPendingLeaveRequests();
  }, [user]);

  const handleSignOut = async () => {
     const auth = getAuth();
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      logger.error('Sign out error', error);
    }
  };

  const confirmLogout = () => {
    setIsLogoutModalOpen(true); 
  };

  const cancelLogout = () => {
    setIsLogoutModalOpen(false); 
  };

  const handleNavigation = (action, href) => {
    if (action === 'signOut') {
      confirmLogout();
    } else if (action === 'navigate') {
      navigate(href);
    }
  };

  const isActiveRoute = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <MoonLoader size={60} color="#4F46E5" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
              {/* Mobile Sidebar */}
              <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
                <Transition show={sidebarOpen}>
                  <div className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-in-out data-[closed]:opacity-0" />
                </Transition>
                <div className="fixed inset-0 flex">
                  <Transition show={sidebarOpen}>
                    <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full">
                      <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gradient-to-b from-indigo-900 via-indigo-800 to-indigo-900 px-6 pb-4 shadow-2xl">
                        <div className="flex h-16 shrink-0 items-center justify-between pt-4">
                          <img className="h-10 w-auto rounded-lg shadow-lg" src={Ayden} alt="AYDEN Garage" />
                          <button
                            type="button"
                            onClick={() => setSidebarOpen(false)}
                            className="-m-2.5 p-2.5 text-indigo-200 hover:text-white transition-colors duration-200"
                          >
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                        <nav className="flex flex-1 flex-col">
                          <ul className="flex flex-1 flex-col gap-y-2">
                            {navigation.map((item) => (
                              <li key={item.name}>
                                <a
                                  href={item.href}
                                  className={classNames(
                                    isActiveRoute(item.href)
                                      ? 'bg-indigo-700 text-white shadow-lg'
                                      : 'text-indigo-200 hover:bg-indigo-700/50 hover:text-white',
                                    'group flex gap-x-3 rounded-xl px-4 py-3 text-sm font-semibold leading-6 transition-all duration-200 transform hover:translate-x-1'
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      isActiveRoute(item.href) ? 'text-white' : 'text-indigo-300 group-hover:text-white',
                                      'h-6 w-6 shrink-0 transition-colors duration-200'
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </nav>
                      </div>
                    </Dialog.Panel>
                  </Transition>
                </div>
              </Dialog>

              <LogoutModal
        isOpen={isLogoutModalOpen}
        onCancel={cancelLogout}
        onConfirm={handleSignOut}
      />

              {/* Desktop Sidebar */}
              <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gradient-to-b from-indigo-900 via-indigo-800 to-indigo-900 px-6 pb-4 shadow-2xl">
                  <div className="flex h-16 shrink-0 items-center pt-4">
                    <img className="h-12 w-auto rounded-lg shadow-lg" src={Ayden} alt="AYDEN Garage" />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul className="flex flex-1 flex-col gap-y-2">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <a
                            href={item.href}
                            className={classNames(
                              isActiveRoute(item.href)
                                ? 'bg-indigo-700 text-white shadow-lg'
                                : 'text-indigo-200 hover:bg-indigo-700/50 hover:text-white',
                              'group flex gap-x-3 rounded-xl px-4 py-3 text-sm font-semibold leading-6 transition-all duration-200 transform hover:translate-x-1'
                            )}
                          >
                            <item.icon
                              className={classNames(
                                isActiveRoute(item.href) ? 'text-white' : 'text-indigo-300 group-hover:text-white',
                                'h-6 w-6 shrink-0 transition-colors duration-200'
                              )}
                              aria-hidden="true"
                            />
                            {item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </div>

                <div className="lg:pl-64">
                  {/* Top Navigation Bar */}
                  <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-indigo-100 bg-white/80 backdrop-blur-xl px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(true)}
                      className="-m-2.5 p-2.5 text-gray-700 lg:hidden hover:text-indigo-600 transition-colors duration-200"
                    >
                      <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                    </button>

                    {/* Separator */}
                    <div className="h-6 w-px bg-gray-200 lg:hidden" aria-hidden="true" />

                    <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6 items-center justify-end">
                      {/* Notification Bell */}
                      <div className="relative">
                        <button className="p-2 rounded-lg hover:bg-indigo-50 transition-colors duration-200">
                          <BellIcon
                            className={classNames(
                              'h-6 w-6 transition-colors duration-200',
                              hasPendingRequests
                                ? 'text-red-500 animate-pulse'
                                : 'text-gray-400 hover:text-indigo-600'
                            )}
                          />
                          {hasPendingRequests && (
                            <span className="absolute top-1.5 right-1.5 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white shadow-lg animate-pulse" />
                          )}
                        </button>
                      </div>

                      {/* Profile Dropdown */}
                      <Menu as="div" className="relative">
                        <Menu.Button className="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-indigo-50 transition-all duration-200">
                          <div className="flex items-center gap-x-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-sm font-bold shadow-lg">
                              {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                            </div>
                            <div className="hidden lg:block text-left">
                              <div className="text-sm font-semibold text-gray-900">
                                {user?.firstName} {user?.lastName}
                              </div>
                              <div className="text-xs text-gray-500">{user?.email}</div>
                            </div>
                          </div>
                          <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden">
                            {userNavigation.map((item) => (
                              <Menu.Item key={item.name}>
                                {({ focus }) => (
                                  <button
                                    onClick={() => handleNavigation(item.action, item.href)}
                                    className={classNames(
                                      focus ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700',
                                      'block w-full text-left px-4 py-3 text-sm font-medium transition-colors duration-200'
                                    )}
                                  >
                                    {item.name}
                                  </button>
                                )}
                              </Menu.Item>
                            ))}
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                  </div>

                  <main className="py-6">
                    <div className="px-4 sm:px-6 lg:px-8">{children}</div>
                  </main>
                </div>
      </div>
    </>
  );
}
